import { showConfirmDialog } from 'vant'

import router from '@/router'
import { useBankStore, useCardStore, useProcessStore } from '@/store'

import {
  sendFindPwdSmsWhenOnlyBindVM,
  validFindPwdInfo,
  validFindPwdInfoWhenNoCard,
  validFindPwdWhenOnlyBindVm,
} from '@/api/findPwd'

import type { SendFindPwdCard, ValidFindPwdCard, ValidFindPwdInfo } from '@/api/findPwd'

import { getMyBankNo } from '@/api/user'
import type { CheckUserInfoParamsAndCode } from '@/api/bindCard'

import Spinner from '@/components/Spinner'

import { useI18n } from '@/hooks/useI18n'

import { BANK_NO, CARD_TYPE, PROCESS } from '@/constant'
import useBindCard from './useBindCard'

export default function useFindPwd() {
  const { t } = useI18n()

  const cardStore = useCardStore()
  const processStore = useProcessStore()

  /**
   * 找回支付密码流程 有三种验证方式找回 实名信息、卡信息和短信验证
   * - 有身份证号
   *   - 绑定了账户=>实名信息
   * - 没有身份证号
   *   - 绑定了卡
   *     - 只有一张卡 银联二维码=>实名信息 其他卡=>卡信息(去掉)
   *     - 渠道是被扫入口的 卡包里有银联二维码的=>实名信息(去掉)
   *     - 有多张卡 获取不是银联二维码的第一张=>卡信息
   *   - 没有绑卡=>短信验证
   */
  const toFindPayPwd = () => {
    const bankStore = useBankStore()
    const processStore = useProcessStore()

    let method = 'push'

    // 如果是绑卡或者修改支付密码这种校验密码的交互就用replace 避免又回退校验密码页面
    // 会因进忘记密码流程而原流程被替换导致进行不下去
    if (
      processStore.process === PROCESS.BIND_CARD ||
      processStore.process === PROCESS.UPDATE_PAY_PWD
    ) {
      method = 'replace'
    }

    processStore.process = PROCESS.FIND_PAY_PWD
    processStore.params.bankNo = bankStore.bankNo

    cardStore.hasRealNameAuth().then((hadRealNameAuth: boolean) => {
      cardStore.hadRealNameAuth = hadRealNameAuth
      // cardStore.hadRealNameAuth = true;

      if (cardStore.hadRealNameAuth) {
        cardStore.cardType = CARD_TYPE.ACCOUNT
        processStore.params.bankNo = BANK_NO.BNU
        router[method]('findPwdValidUser')
        return
      }

      cardStore.hasBindCard().then((hadBindCard: boolean) => {
        cardStore.hadBindCard = hadBindCard

        if (cardStore.hadBindCard) {
          getMyBankNo().then((res) => {
            const bindBankNos = res.result

            processStore.params.bankNo = bindBankNos[0]
            processStore.params.isSelectBankNo = true
            router[method]('findPwdValidCard')
          })
        } else {
          router[method]('findPwdValidCode')
        }
      })
    })
  }

  /** 校验实名信息方式 */
  const checkFindPwdAllByAccount = (form: CheckUserInfoParamsAndCode) => {
    const { bankNo, cardNo, name, birthday, idType, idNo, code } = form

    const params: ValidFindPwdInfo = { bankNo, cardNo, name, birthday, idType, idNo, code }

    Spinner.loading()

    validFindPwdInfo(params)
      .then(() => {
        router.replace('setPwd')
      })
      .finally(() => Spinner.close())
  }

  /** 校验卡信息方式 */
  const checkFindPwdInfoByCard = (form: CheckUserInfoParamsAndCode) => {
    Spinner.loading()

    const { bankNo, cardNo, phoneArea, phone, expiryDate, name } = form
    const params: SendFindPwdCard = {
      expiryDate,
      name,
      bankNo,
      cardNo,
      phoneArea,
      phone,
    }

    sendFindPwdSmsWhenOnlyBindVM(params)
      .then(() => {
        processStore.params = params
        router.replace('findPwdValidCode')
      })
      .finally(() => Spinner.close())
  }

  /** 校验卡信息方式的手机验证码 */
  const checkFindPwdCodeByCard = (code) => {
    const { validCardInfoParams } = useBindCard()
    const params: ValidFindPwdCard = {
      ...(<SendFindPwdCard>processStore.params),
      code,
    }

    Spinner.loading()

    validFindPwdWhenOnlyBindVm(validCardInfoParams(params))
      .then(() => {
        router.replace('setPwd')
      })
      .finally(() => Spinner.close())
  }

  /** 校验短信验证方式 */
  const checkFindPwdCodeByNoCard = (code) => {
    const params = {
      code,
    }

    Spinner.loading()

    validFindPwdInfoWhenNoCard(params)
      .then(() => {
        router.replace('setPwd')
      })
      .finally(() => Spinner.close())
  }

  const onBack = () => {
    showConfirmDialog({
      title: t('common.tipsText'),
      message: t('dialog.abandonFindPwd'),
    }).then(() => {
      history.go(-1)
    })
  }

  return {
    toFindPayPwd,
    checkFindPwdAllByAccount,
    checkFindPwdInfoByCard,
    checkFindPwdCodeByCard,
    checkFindPwdCodeByNoCard,
    onBack,
  }
}

// bCode: "5048"
// bMsg: "不允许此方式找回密码"
